import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface ImageInfo {
  type: string;
  label: string;
  thumbnail: string;
  full: string;
  reference: string;
}

interface ImageCarouselProps {
  images: ImageInfo[];
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light rounded" 
           style={{ height: '200px' }}>
        <div className="text-center text-muted">
          <i className="fas fa-image fa-3x mb-2"></i>
          <div>{t('products.details.noImages')}</div>
        </div>
      </div>
    );
  }

  const currentImage = images[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleImageClick = () => {
    setShowLightbox(true);
  };

  return (
    <>
      <div className="position-relative">
        {/* Main carousel container */}
        <div className="image-carousel-container rounded overflow-hidden bg-light d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
          <img
            src={currentImage.thumbnail}
            alt={currentImage.label}
            className="cursor-pointer"
            style={{
              maxHeight: '200px',
              maxWidth: '100%',
              objectFit: 'contain',
              cursor: 'zoom-in'
            }}
            onClick={handleImageClick}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudGFsIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNmM3NTdkIj5ObyBpbWFnZTwvdGV4dD48L3N2Zz4=';
            }}
          />
          
          {/* Navigation arrows (only show if more than 1 image) */}
          {images.length > 1 && (
            <>
              <button
                className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y ms-2"
                style={{ opacity: 0.8 }}
                onClick={handlePrevious}
                type="button"
                aria-label="Previous image"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ opacity: 0.8 }}
                onClick={handleNext}
                type="button"
                aria-label="Next image"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}

          {/* Zoom icon overlay */}
          <div 
            className="position-absolute top-0 end-0 m-2"
            style={{ opacity: 0.8 }}
          >
            <i className="fas fa-search-plus text-white"></i>
          </div>

          {/* Image counter (only show if more than 1 image) */}
          {images.length > 1 && (
            <div 
              className="position-absolute bottom-0 start-50 translate-middle-x mb-2 px-2 py-1 rounded"
              style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '0.8rem' }}
            >
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Modal
        show={showLightbox}
        onHide={() => setShowLightbox(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title>{currentImage.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <img
            src={currentImage.full}
            alt={currentImage.label}
            className="img-fluid w-100"
            style={{ maxHeight: '80vh', objectFit: 'contain' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = currentImage.thumbnail; // Fallback to thumbnail
            }}
          />
          {images.length > 1 && (
            <div className="position-absolute top-50 w-100 d-flex justify-content-between px-3">
              <button
                className="btn btn-dark btn-lg"
                onClick={handlePrevious}
                type="button"
                style={{ opacity: 0.8 }}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn btn-dark btn-lg"
                onClick={handleNext}
                type="button"
                style={{ opacity: 0.8 }}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImageCarousel;